import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, RefreshCw } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Spinner } from "@/shared/components/ui/spinner";
import { cleanDocument } from "@/shared/lib/document";
import { formatCurrency } from "@/shared/lib/formatters";
import {
  getReadableErrorMessage,
  isUnconfirmedRequest,
  parseSefazMessage,
} from "@/shared/lib/sefaz-message";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { commercialInvoiceService } from "@/features/commercial-invoices/services/commercial-invoice.service";
import {
  COMMERCIAL_INVOICE_PAGE_SIZES,
  useCommercialInvoiceList,
} from "@/features/commercial-invoices/hooks/useCommercialInvoiceList";
import { CommercialInvoiceFilters } from "@/features/commercial-invoices/components/CommercialInvoiceFilters";
import { CommercialInvoicesTable } from "@/features/commercial-invoices/components/CommercialInvoicesTable";
import { TablePagination } from "@/features/commercial-invoices/components/TablePagination";
import { CommercialInvoiceEmitDialog } from "@/features/commercial-invoices/components/CommercialInvoiceEmitDialog";
import { CommercialInvoiceCancelDialog } from "@/features/commercial-invoices/components/CommercialInvoiceCancelDialog";
import {
  COMMERCIAL_INVOICE_PAYMENT_METHOD_LABELS,
  COMMERCIAL_INVOICE_STATUS_LABELS,
} from "@/features/commercial-invoices/components/CommercialInvoiceStatusBadge";
import type {
  CommercialInvoiceRequestDTO,
  CommercialInvoiceResponseDTO,
} from "@/features/commercial-invoices/types/commercial-invoice.types";

export function NotasComerciaisPage() {
  const list = useCommercialInvoiceList();
  const { updateLocal, setFilters } = list;

  const [emitOpen, setEmitOpen] = useState(false);
  const [emitLoading, setEmitLoading] = useState(false);
  const [emitResult, setEmitResult] =
    useState<CommercialInvoiceResponseDTO | null>(null);
  const [emitRejection, setEmitRejection] = useState<string | null>(null);

  const [invoiceToCancel, setInvoiceToCancel] =
    useState<CommercialInvoiceResponseDTO | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [refreshingId, setRefreshingId] = useState<number | null>(null);
  const [quickFilter, setQuickFilter] = useState("");

  const openEmitDialog = () => {
    setEmitResult(null);
    setEmitRejection(null);
    setEmitOpen(true);
  };

  const closeEmitDialog = () => {
    setEmitOpen(false);
    setEmitResult(null);
    setEmitRejection(null);
  };

  const handleEmit = async (data: CommercialInvoiceRequestDTO) => {
    setEmitLoading(true);
    setEmitRejection(null);
    try {
      const invoice = await commercialInvoiceService.emit(data);
      if (invoice.status === "ERROR") {
        const rejection = parseSefazMessage(invoice.sefazMessage);
        const code = invoice.sefazStatus ?? rejection?.code;
        const reason =
          rejection?.message ??
          "A SEFAZ recusou a nota. Confira os dados e emita novamente.";
        setEmitRejection(code ? `${reason} (código ${code})` : reason);
      } else {
        setEmitResult(invoice);
        if (invoice.status === "ISSUED") {
          toast.success("Nota autorizada com sucesso.");
        } else {
          toast.warning(
            "Nota registrada, mas ainda sem confirmação. Use Reconsultar antes de emitir outra.",
          );
        }
      }
    } catch (error: unknown) {
      setEmitRejection(null);
      toast.error(
        isUnconfirmedRequest(error)
          ? "A emissão não foi confirmada. Verifique na listagem se a nota foi registrada antes de emitir outra."
          : getReadableErrorMessage(
              error,
              "Não foi possível emitir a nota. Tente novamente.",
            ),
      );
    } finally {
      setEmitLoading(false);
      list.reload();
    }
  };

  const handleRefresh = useCallback(
    async (invoice: CommercialInvoiceResponseDTO) => {
      setRefreshingId(invoice.id);
      try {
        const updated = await commercialInvoiceService.refresh(invoice.id);
        updateLocal(updated);
        toast.success("Situação da nota atualizada.");
      } catch (error: unknown) {
        toast.error(
          getReadableErrorMessage(
            error,
            "Não foi possível atualizar a situação da nota. Tente novamente.",
          ),
        );
      } finally {
        setRefreshingId(null);
      }
    },
    [updateLocal],
  );

  const clearFilters = useCallback(() => setFilters({}), [setFilters]);

  const handleCancelConfirm = async (justification: string) => {
    if (!invoiceToCancel) return;
    setCancelLoading(true);
    try {
      const updated = await commercialInvoiceService.cancel(
        invoiceToCancel.id,
        { justification },
      );
      updateLocal(updated);
      toast.success("Nota cancelada com sucesso.");
      setInvoiceToCancel(null);
    } catch (error: unknown) {
      toast.error(
        isUnconfirmedRequest(error)
          ? "O cancelamento não foi confirmado. Confira a situação da nota na listagem antes de tentar de novo."
          : getReadableErrorMessage(
              error,
              "Não foi possível cancelar a nota. Tente novamente.",
            ),
      );
      setInvoiceToCancel(null);
      list.reload();
    } finally {
      setCancelLoading(false);
    }
  };

  const visibleInvoices = useMemo(() => {
    const term = quickFilter.trim().toLowerCase();
    if (!term) return list.data;
    const document = cleanDocument(term);
    return list.data.filter((invoice) => {
      const haystack = [
        String(invoice.id),
        invoice.buyerName,
        invoice.invoiceNumber,
        invoice.nfeKey,
        invoice.ref,
        formatCurrency(invoice.value),
        COMMERCIAL_INVOICE_STATUS_LABELS[invoice.status],
        COMMERCIAL_INVOICE_PAYMENT_METHOD_LABELS[invoice.paymentMethod],
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (haystack.includes(term)) return true;
      return (
        document.length > 0 &&
        Boolean(invoice.buyerDocument?.includes(document))
      );
    });
  }, [list.data, quickFilter]);

  const isEmpty = !list.loading && !list.error && list.data.length === 0;
  const hasFilters = Boolean(list.filters.date) || Boolean(list.filters.status);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Notas Comerciais
          </h1>
          <p className="text-muted-foreground">
            Emissão avulsa de NFC-e, com item único e sem vínculo com locação.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 self-start md:self-auto">
          <Can permission={ACTIONS.commercialInvoices.create}>
            <Button size="sm" onClick={openEmitDialog} className="gap-2">
              <Plus className="w-4 h-4" />
              Emitir NFC-e
            </Button>
          </Can>
          <Button
            variant="outline"
            size="sm"
            onClick={list.reload}
            disabled={list.loading}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </Button>
        </div>
      </div>

      <CommercialInvoiceFilters
        filters={list.filters}
        loading={list.loading}
        onApply={setFilters}
        onClear={clearFilters}
      />

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle>Notas emitidas</CardTitle>
              <CardDescription>
                {list.loading
                  ? "Carregando..."
                  : `Mostrando ${visibleInvoices.length} de ${list.totalElements} notas`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Filtrar na tabela..."
                value={quickFilter}
                onChange={(e) => setQuickFilter(e.target.value)}
                className="md:max-w-xs"
                aria-label="Filtrar as notas carregadas nesta página"
              />
              <select
                value={list.pageSize}
                onChange={(e) => list.setPageSize(Number(e.target.value))}
                disabled={list.loading}
                aria-label="Notas por página"
                className="h-9 cursor-pointer rounded-md border border-input bg-background px-3 py-1 text-sm shrink-0 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {COMMERCIAL_INVOICE_PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size} / página
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {list.loading && list.data.length === 0 ? (
            <div className="flex justify-center items-center py-16">
              <Spinner className="w-6 h-6" />
            </div>
          ) : list.error ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <p className="text-destructive">{list.error}</p>
              <Button variant="outline" size="sm" onClick={list.reload}>
                Tentar novamente
              </Button>
            </div>
          ) : isEmpty ? (
            <div className="py-16 text-center text-muted-foreground">
              {hasFilters
                ? "Nenhuma nota comercial encontrada para os filtros aplicados."
                : "Nenhuma nota comercial emitida ainda."}
            </div>
          ) : (
            <>
              {visibleInvoices.length === 0 ? (
                <div className="py-16 text-center text-muted-foreground">
                  Nenhuma nota nesta página corresponde a "{quickFilter}".
                </div>
              ) : (
                <CommercialInvoicesTable
                  invoices={visibleInvoices}
                  refreshingId={refreshingId}
                  onRefresh={handleRefresh}
                  onCancel={setInvoiceToCancel}
                />
              )}
              <TablePagination
                currentPage={list.page}
                totalPages={list.totalPages}
                loading={list.loading}
                onPageChange={list.setPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      {emitOpen && (
        <CommercialInvoiceEmitDialog
          open
          loading={emitLoading}
          result={emitResult}
          rejection={emitRejection}
          onClose={closeEmitDialog}
          onSubmit={handleEmit}
        />
      )}

      {invoiceToCancel && (
        <CommercialInvoiceCancelDialog
          open
          loading={cancelLoading}
          invoice={invoiceToCancel}
          onClose={() => setInvoiceToCancel(null)}
          onConfirm={handleCancelConfirm}
        />
      )}
    </div>
  );
}
